import { useCallback, useState } from 'react';
import { AdjustFxRateDialog } from 'src/dialogs/AdjustFxRate/AdjustFxRateDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature, PurchaseOrderDetails } from 'src/WebApiController';

/**
 * Hook for the "Adjust FX Rate" action on a single Purchase Order.
 *
 * Visible when BOTH conditions are true:
 *  1. The `AdjustPurchaseFxRate` feature flag is enabled.
 *  2. The purchase order has been FX-converted, indicated by
 *     `purchaseOrder.originalCurrencyCode` being non-null.
 *
 * After conversion, `purchaseOrder.currency` holds the target (account)
 * currency, so no separate `accountCurrency` lookup is needed.
 */
export const useAdjustFxRate = (
  purchaseOrder: PurchaseOrderDetails | null,
  onApplySuccess?: () => void
) => {
  const hasFeature = useUserHasFeature(Feature.AdjustPurchaseFxRate);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isVisible = !!hasFeature && purchaseOrder?.originalCurrencyCode != null;

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleSuccess = useCallback(() => {
    closeDialog();
    onApplySuccess?.();
  }, [closeDialog, onApplySuccess]);

  const dialog =
    isDialogOpen && purchaseOrder != null ? (
      <AdjustFxRateDialog
        isOpen={isDialogOpen}
        purchaseOrder={purchaseOrder}
        onSuccess={handleSuccess}
        onCancel={closeDialog}
      />
    ) : null;

  return {
    isVisible,
    openDialog,
    dialog,
  };
};
